import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// ============================================
// TYPES
// ============================================

export interface GlobalPolicy {
  minCoverageRatioThreshold: number;
  requireCustodyForDeploy: boolean;
  requireCustodyForMint: boolean;
  autoUpdatePoROnSupplyChange: boolean;
  requireLedgerIntegrationForYield: boolean;
  allowTenantOverride: boolean;
}

export interface TenantPolicy {
  coverageRatioThreshold?: number;
  requireCustodyForDeploy?: boolean;
  requireCustodyForMint?: boolean;
  autoUpdatePoROnSupplyChange?: boolean;
  requireLedgerIntegrationForYield?: boolean;
}

export interface EffectivePolicy {
  minCoverageRatioThreshold: number;
  requireCustodyForDeploy: boolean;
  requireCustodyForMint: boolean;
  autoUpdatePoROnSupplyChange: boolean;
  requireLedgerIntegrationForYield: boolean;
  isCustomized: boolean;
  customizations: string[];
}

export interface TenantPolicyResponse {
  globalPolicy: GlobalPolicy;
  tenantPolicy: TenantPolicy | null;
  effectivePolicy: EffectivePolicy;
}

export interface TenantWithOverride {
  tenantId: string;
  tenantName: string;
  customizations: string[];
}

export interface AssetHealthDetail {
  assetId: string;
  assetName: string;
  symbol: string;
  coverageRatio: number;
  healthStatus: 'HEALTHY' | 'WARNING' | 'CRITICAL';
  lastPoRUpdate: string | null;
  custodyStatus: string | null;
  issues: string[];
}

export interface HealthSummary {
  tenantId: string;
  totalAssets: number;
  healthyAssets: number;
  warningAssets: number;
  criticalAssets: number;
  assetHealthDetails: AssetHealthDetail[];
  pendingYieldDistributions: number;
  pendingYieldAmount: string;
  failedYieldPayments: number;
  failedYieldAmount: string;
  lastUpdated: string;
}

export interface TenantSummary {
  tenantId: string;
  tenantName: string;
  healthy: number;
  warning: number;
  critical: number;
}

export interface PlatformHealthSummary {
  totalAssets: number;
  healthyAssets: number;
  warningAssets: number;
  criticalAssets: number;
  tenantSummaries: TenantSummary[];
  pendingYieldDistributions: number;
  failedYieldPayments: number;
  lastUpdated: string;
}

export interface FinancialAlert {
  id: string;
  tenantId: string;
  assetId: string | null;
  type: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  title: string;
  message: string;
  data: Record<string, any>;
  acknowledged: boolean;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  createdAt: string;
}

export interface AuditLogEntry {
  id: string;
  tenantId: string;
  userId: string;
  userName: string;
  action: string;
  entityType: string;
  entityId: string;
  assetId: string | null;
  assetName: string | null;
  result: 'SUCCESS' | 'FAILURE';
  details: Record<string, any>;
  createdAt: string;
}

export interface DeployPrerequisites {
  canDeploy: boolean;
  assetApproved: boolean;
  custodyExists: boolean;
  custodyActive: boolean;
  custodyDetails?: {
    id: string;
    status: string;
    custodianName: string;
    custodyReference: string;
    verifiedAt?: string;
  };
  blockingIssues: string[];
  policy: EffectivePolicy;
}

export interface MintPrerequisites {
  canMint: boolean;
  custodyActive: boolean;
  currentCoverageRatio: number;
  projectedCoverageRatio: number;
  wouldUndercollateralize: boolean;
  currentSupply: string;
  projectedSupply: string;
  custodyValue: string;
  blockingIssues: string[];
  policy: EffectivePolicy;
}

// ============================================
// POLICY HOOKS
// ============================================

export function useGlobalPolicy() {
  return useQuery({
    queryKey: ['financial-security', 'global-policy'],
    queryFn: async () => {
      const { data } = await api.get<GlobalPolicy>('/tokenization/financial-security/global-policy');
      return data;
    },
  });
}

export function useUpdateGlobalPolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (policy: Partial<GlobalPolicy>) => {
      const { data } = await api.put<GlobalPolicy>('/tokenization/financial-security/global-policy', policy);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-security', 'global-policy'] });
    },
  });
}

export function useTenantPolicy() {
  return useQuery({
    queryKey: ['financial-security', 'tenant-policy'],
    queryFn: async () => {
      const { data } = await api.get<TenantPolicyResponse>('/tokenization/financial-security/tenant-policy');
      return data;
    },
  });
}

export function useUpdateTenantPolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (policy: TenantPolicy) => {
      const { data } = await api.put<TenantPolicy>('/tokenization/financial-security/tenant-policy', policy);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-security', 'tenant-policy'] });
    },
  });
}

export function useResetTenantPolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.delete('/tokenization/financial-security/tenant-policy');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-security', 'tenant-policy'] });
    },
  });
}

export function useTenantsWithOverrides() {
  return useQuery({
    queryKey: ['financial-security', 'tenants-with-overrides'],
    queryFn: async () => {
      const { data } = await api.get<TenantWithOverride[]>('/tokenization/financial-security/tenants-with-overrides');
      return data;
    },
  });
}

// ============================================
// HEALTH HOOKS
// ============================================

export function useHealthSummary() {
  return useQuery({
    queryKey: ['financial-security', 'health-summary'],
    queryFn: async () => {
      const { data } = await api.get<HealthSummary>('/tokenization/financial-security/health-summary');
      return data;
    },
    refetchInterval: 60000, // Refresh every minute
  });
}

export function usePlatformHealthSummary() {
  return useQuery({
    queryKey: ['financial-security', 'platform-health-summary'],
    queryFn: async () => {
      const { data } = await api.get<PlatformHealthSummary>('/tokenization/financial-security/platform-health-summary');
      return data;
    },
    refetchInterval: 60000,
  });
}

// ============================================
// ALERT HOOKS
// ============================================

export function useFinancialAlerts(filters?: { acknowledged?: boolean; severity?: string }) {
  return useQuery({
    queryKey: ['financial-security', 'alerts', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.acknowledged !== undefined) params.append('acknowledged', String(filters.acknowledged));
      if (filters?.severity) params.append('severity', filters.severity);

      const { data } = await api.get<FinancialAlert[]>(`/tokenization/financial-security/alerts?${params}`);
      return data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

export function useUnacknowledgedAlertCount() {
  const { data: alerts } = useFinancialAlerts({ acknowledged: false });
  return alerts?.length ?? 0;
}

export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (alertId: string) => {
      const { data } = await api.post(`/tokenization/financial-security/alerts/${alertId}/acknowledge`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-security', 'alerts'] });
    },
  });
}

// ============================================
// AUDIT HOOKS
// ============================================

export function useSecurityAuditLog(filters?: {
  entityType?: string;
  assetId?: string;
  result?: string;
  skip?: number;
  take?: number;
}) {
  return useQuery({
    queryKey: ['financial-security', 'audit-log', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.entityType) params.append('entityType', filters.entityType);
      if (filters?.assetId) params.append('assetId', filters.assetId);
      if (filters?.result) params.append('result', filters.result);
      if (filters?.skip !== undefined) params.append('skip', String(filters.skip));
      if (filters?.take !== undefined) params.append('take', String(filters.take));

      const { data } = await api.get<{ logs: AuditLogEntry[]; total: number }>(
        `/tokenization/financial-security/audit-log?${params}`
      );
      return data;
    },
  });
}

// ============================================
// PREREQUISITES HOOKS
// ============================================

export function useDeployPrerequisites(assetId: string | undefined) {
  return useQuery({
    queryKey: ['financial-security', 'deploy-prerequisites', assetId],
    queryFn: async () => {
      if (!assetId) return null;
      const { data } = await api.get<DeployPrerequisites>(
        `/tokenization/financial-security/deploy-prerequisites/${assetId}`
      );
      return data;
    },
    enabled: !!assetId,
  });
}

export function useMintPrerequisites(assetId: string | undefined, amount: string | undefined) {
  return useQuery({
    queryKey: ['financial-security', 'mint-prerequisites', assetId, amount],
    queryFn: async () => {
      if (!assetId || !amount) return null;
      const { data } = await api.get<MintPrerequisites>(
        `/tokenization/financial-security/mint-prerequisites/${assetId}?amount=${amount}`
      );
      return data;
    },
    enabled: !!assetId && !!amount,
  });
}
